import { Auth0Client, IdToken } from '@auth0/auth0-spa-js';
import SessionClient, { IdentityNotLinkedError, SessionResult } from '../helpers/sessionsApiClient';
import handleRedirectCallback from './callback/handleRedirectCallback';
import navigateTo from '~app/helpers/navigateTo';
import { fetchAndRemoveEp, fetchAndRemoveSm, fetchAndRemoveSmAllow, stashIdToken } from '../helpers/sessionStorage';
import { Auth0Config } from '~app/helpers/auth0Config';
import cleanObject from '~common/utils/cleanObject';
import { SmParams } from '~common/entities/smParams';
import { AppState } from './handlers';
import RespondentSSOClient from '../helpers/respondentSSOApiClient';

function handleSsoSessionResult(result: SessionResult, redirectUrl?: string): void {
  if (redirectUrl) {
    navigateTo(redirectUrl);
  } else {
    throw new Error(`redirectUrl not provided for SSO_LOGIN result: ${result}`);
  }
}

function handleLoggedInOrCreatedSessionResult(
  result: SessionResult,
  appState: AppState,
  ep?: string,
  sm?: string
): void {
  navigateTo(
    '/login/next',
    cleanObject({
      ep,
      joined: result === SessionResult.CREATED ? 'true' : undefined,
      force_account_picker: appState.forceAccountPicker ? 'true' : undefined,
      sm,
    })
  );
}

function handleUnlinkedSessionResult(
  appState: AppState,
  claims: IdToken,
  error?: IdentityNotLinkedError,
  ep?: string,
  sm?: string,
  smAllowCreateUser?: string
): void {
  const { app, forceAccountPicker, ut_source: utSource, ut_source2: utSource2 } = appState;
  const emailInUse = error?.emailInUse ? 'true' : undefined;
  stashIdToken(claims);
  navigateTo(
    '/login/unlinked',
    cleanObject({
      [SmParams.SM_APP]: app,
      [SmParams.SM_FORCE_ACCOUNT_PICKER]: forceAccountPicker ? '' : undefined,
      ep,
      sm,
      sm_allow_create_user: smAllowCreateUser,
      ut_source: utSource,
      ut_source2: utSource2,
      email_in_use: emailInUse,
    })
  );
}

/**
 * Completes a login transaction and establishes a session, then navigates the user
 *
 * @param config Auth0 configuration
 */
async function auth0Callback(
  config: Auth0Config,
  ep?: string,
  sm?: string,
  sm_allow_create_user?: string
): Promise<void> {
  const { domain, clientId } = config;
  if (!domain || !clientId) {
    throw new Error('Missing auth0 configuration');
  }

  const client = new Auth0Client({
    clientId,
    domain,
    nowProvider: () => config.now,
  });
  const { appState, claims } = await handleRedirectCallback(client);
  const { result, error, redirectUrl } = await SessionClient().createByLogin({
    claims,
    appState,
    smAllowCreateUser: sm_allow_create_user,
  });

  switch (result) {
    case SessionResult.SSO_LOGIN:
      handleSsoSessionResult(result, redirectUrl);
      break;
    case SessionResult.LOGGED_IN:
    case SessionResult.CREATED:
      handleLoggedInOrCreatedSessionResult(result, appState, ep, sm);
      break;
    case SessionResult.UNLINKED:
      handleUnlinkedSessionResult(appState, claims, error, ep, sm, sm_allow_create_user);
      break;
    default:
      throw new Error(`Unhandled result: ${result}`);
  }
}

export default async function callback(config: Auth0Config, isLegacySso: boolean): Promise<void> {
  const ep = fetchAndRemoveEp() ?? undefined;
  const sm = fetchAndRemoveSm() ?? undefined;

  if (ep?.includes('sso_user')) {
    const { domain, clientId } = config;
    if (!domain || !clientId) {
      throw new Error('Missing auth0 configuration');
    }
    const client = new Auth0Client({
      clientId,
      domain,
      nowProvider: () => config.now,
    });
    const { claims } = await handleRedirectCallback(client);
    const respondentSSOData = await RespondentSSOClient().createRespondentSSOToken(claims, ep);
    navigateTo(respondentSSOData.redirectUrl ?? ep);
  } else if (isLegacySso) {
    navigateTo('/user/redirect', cleanObject({ ep, sm }));
  } else {
    const sm_allow_create_user = fetchAndRemoveSmAllow() ?? undefined;
    await auth0Callback(config, ep, sm, sm_allow_create_user);
  }
}
